import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Webhook as WebhookIcon, RefreshCw, Eye, RotateCw } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, { controlClass, secondaryButtonClass } from '../components/FilterBar';
import { formatDateTime } from '../lib/format';
import {
  fetchWebhooks,
  fetchWebhook,
  retryWebhook,
  type WebhookEvent,
} from '../lib/api';

const PAGE_SIZE = 20;
const SOURCES = [
  'mercury',
  'btcpay',
  'paypal',
  'chain2pay',
  'paymento',
  'apideck',
  'stripe',
  'documenso',
];
const STATUSES = ['received', 'processed', 'failed', 'dead_letter', 'not_configured'];

function Webhooks() {
  const [rows, setRows] = useState<WebhookEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [retrying, setRetrying] = useState<Set<number>>(new Set());
  const [detail, setDetail] = useState<WebhookEvent | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchWebhooks({
      page,
      page_size: PAGE_SIZE,
      source: source || undefined,
      status: status || undefined,
    });
    if (result.ok && result.data) {
      setRows(result.data.items ?? []);
      setTotal(result.data.total ?? 0);
    } else {
      setRows([]);
      setTotal(0);
    }
    setLoading(false);
  }, [page, source, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (id: number) => {
    setRetrying((prev) => new Set(prev).add(id));
    const result = await retryWebhook(id);
    if (result.ok) {
      toast.success('Webhook retried');
      await load();
    }
    setRetrying((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const openDetail = async (event: WebhookEvent) => {
    setDetail(event);
    const result = await fetchWebhook(event.id);
    if (result.ok && result.data) setDetail(result.data);
  };

  const columns: Column<WebhookEvent>[] = [
    { key: 'received_at', header: 'Received', render: (r) => formatDateTime(r.received_at) },
    { key: 'source', header: 'Source', render: (r) => <StatusPill status={r.source} tone="neutral" label={r.source} /> },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
    { key: 'signature_status', header: 'Signature', render: (r) => <StatusPill status={r.signature_status} /> },
    { key: 'retry_count', header: 'Retries', align: 'right', render: (r) => String(r.retry_count) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDetail(r);
            }}
            className={secondaryButtonClass}
          >
            <Eye size={14} /> View
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRetry(r.id);
            }}
            disabled={retrying.has(r.id)}
            className={secondaryButtonClass}
          >
            <RotateCw size={14} className={retrying.has(r.id) ? 'animate-spin' : ''} /> Retry
          </button>
        </div>
      ),
    },
  ];

  const applyFilters = () => {
    setPage(1);
    load();
  };

  return (
    <PageShell
      title="Webhooks"
      subtitle="Inbound webhook events from payment providers, with signature status and retries."
    >
      <FilterBar
        actions={
          <button type="button" onClick={applyFilters} className={secondaryButtonClass}>
            <RefreshCw size={16} /> Apply
          </button>
        }
      >
        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(1);
          }}
          className={controlClass}
        >
          <option value="">All sources</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className={controlClass}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => String(r.id)}
        loading={loading}
        onRowClick={openDetail}
        emptyTitle="No webhook events"
        emptyMessage="Inbound webhooks from Mercury, BTCPay, PayPal, and Chain2Pay will appear here."
        emptyIcon={<WebhookIcon size={32} />}
      />

      {total > PAGE_SIZE && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.source} webhook #${detail.id}` : 'Webhook'}
        size="lg"
      >
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status" value={<StatusPill status={detail.status} />} />
              <Field label="Signature" value={<StatusPill status={detail.signature_status} />} />
              <Field label="Received" value={formatDateTime(detail.received_at)} />
              <Field label="Processed" value={detail.processed_at ? formatDateTime(detail.processed_at) : '—'} />
              <Field label="Event ID" value={detail.event_id || '—'} />
              <Field label="Retries" value={String(detail.retry_count)} />
            </div>
            {detail.error_message && (
              <div>
                <p className="mb-1 text-zt-muted">Error</p>
                <pre className="overflow-auto rounded-lg bg-zt-danger/10 p-3 font-mono text-xs text-zt-danger">
                  {detail.error_message}
                </pre>
              </div>
            )}
            <div>
              <p className="mb-1 text-zt-muted">Headers</p>
              <pre className="max-h-40 overflow-auto rounded-lg bg-zt-bg p-3 font-mono text-xs text-zt-muted">
                {JSON.stringify(detail.headers ?? {}, null, 2)}
              </pre>
            </div>
            <div>
              <p className="mb-1 text-zt-muted">Metadata</p>
              <pre className="max-h-60 overflow-auto rounded-lg bg-zt-bg p-3 font-mono text-xs text-zt-muted">
                {JSON.stringify(detail.metadata ?? {}, null, 2)}
              </pre>
            </div>
            {detail.payload_path && (
              <Field label="Payload path" value={<span className="font-mono text-xs">{detail.payload_path}</span>} />
            )}
          </div>
        )}
      </Modal>
    </PageShell>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-zt-muted">{label}</p>
      <div className="mt-0.5 text-slate-200">{value}</div>
    </div>
  );
}

export default Webhooks;
